/**
 * Form Validator Utility
 * Validates that form fields match template variables
 */
class FormValidator {
  /**
   * Extract variables from mustache template
   * @param {string} template - Mustache template string
   * @returns {Set} Set of variable names found in template
   */
  extractTemplateVariables(template) {
    const variableRegex = /\{\{([^}]+)\}\}/g;
    const arrayRegex = /\{\{#([^}]+)\}\}/g;
    const variables = new Set();

    let match;
    // Extract regular variables
    while ((match = variableRegex.exec(template)) !== null) {
      const variableName = match[1].trim();
      if (
        !variableName.startsWith('#') &&
        !variableName.startsWith('/') &&
        variableName !== '.'
      ) {
        variables.add(variableName);
      }
    }

    // Extract array variables
    template.replace(arrayRegex, (match, variableName) => {
      variables.add(variableName.trim());
      return match;
    });

    return variables;
  }

  /**
   * Validate that form schema matches template variables
   * @param {Object} formSchema - Form schema object
   * @param {string} template - Mustache template string
   * @returns {Object} Validation result
   */
  validateFormSchema(formSchema, template) {
    const templateVariables = this.extractTemplateVariables(template);
    const formFields = new Set(formSchema.fields.map(field => field.name));

    const missingInForm = [...templateVariables].filter(
      variable => !formFields.has(variable)
    );
    const missingInTemplate = [...formFields].filter(
      field => !templateVariables.has(field)
    );

    const isValid =
      missingInForm.length === 0 && missingInTemplate.length === 0;

    return {
      isValid,
      templateVariables: [...templateVariables].sort(),
      formFields: [...formFields].sort(),
      missingInForm,
      missingInTemplate,
      errors: this._generateErrors(missingInForm, missingInTemplate),
    };
  }

  /**
   * Validate form data against schema
   * @param {Object} formData - Form data to validate
   * @param {Object} formSchema - Form schema
   * @returns {Object} Validation result
   */
  validateFormData(formData, formSchema) {
    const errors = {};
    let isValid = true;

    formSchema.fields.forEach(field => {
      const value = formData[field.name];
      const fieldErrors = this._validateField(field, value);

      if (fieldErrors.length > 0) {
        errors[field.name] = fieldErrors;
        isValid = false;
      }
    });

    return {
      isValid,
      errors,
      validatedData: this._cleanFormData(formData, formSchema),
    };
  }

  /**
   * Validate individual field
   * @param {Object} fieldSchema - Field schema
   * @param {*} value - Field value
   * @returns {Array} Array of error messages
   */
  _validateField(fieldSchema, value) {
    const errors = [];

    // Required validation
    if (fieldSchema.required && this._isEmpty(value)) {
      errors.push(`${fieldSchema.label} is required`);
      return errors; // Don't continue if required field is empty
    }

    // Skip other validations if field is empty and not required
    if (this._isEmpty(value) && !fieldSchema.required) {
      return errors;
    }

    // Type-specific validations
    if (fieldSchema.validation) {
      const validation = fieldSchema.validation;

      if (fieldSchema.type === 'array') {
        if (validation.minItems && value.length < validation.minItems) {
          errors.push(
            `${fieldSchema.label} must have at least ${validation.minItems} items`
          );
        }
      } else if (
        fieldSchema.type === 'textarea' ||
        fieldSchema.type === 'text'
      ) {
        if (validation.minLength && value.length < validation.minLength) {
          errors.push(
            `${fieldSchema.label} must be at least ${validation.minLength} characters`
          );
        }
      }
    }

    return errors;
  }

  /**
   * Check if value is empty
   * @param {*} value - Value to check
   * @returns {boolean} True if empty
   */
  _isEmpty(value) {
    if (value === null || value === undefined || value === '') {
      return true;
    }
    if (Array.isArray(value)) {
      return value.length === 0 || value.every(item => this._isEmpty(item));
    }
    return false;
  }

  /**
   * Clean form data by removing empty values
   * @param {Object} formData - Form data
   * @param {Object} formSchema - Form schema
   * @returns {Object} Cleaned data
   */
  _cleanFormData(formData, formSchema) {
    const cleaned = {};

    formSchema.fields.forEach(field => {
      const value = formData[field.name];

      if (field.type === 'array') {
        cleaned[field.name] = Array.isArray(value)
          ? value.filter(item => !this._isEmpty(item))
          : [];
      } else {
        cleaned[field.name] = this._isEmpty(value) ? '' : value;
      }
    });

    return cleaned;
  }

  /**
   * Generate error messages for validation results
   * @param {Array} missingInForm - Variables missing in form
   * @param {Array} missingInTemplate - Fields missing in template
   * @returns {Array} Array of error messages
   */
  _generateErrors(missingInForm, missingInTemplate) {
    const errors = [];

    if (missingInForm.length > 0) {
      errors.push(
        `Form is missing fields for template variables: ${missingInForm.join(', ')}`
      );
    }

    if (missingInTemplate.length > 0) {
      errors.push(
        `Form has fields not used in template: ${missingInTemplate.join(', ')}`
      );
    }

    return errors;
  }

  /**
   * Load form schema from JSON file
   * @param {string} schemaPath - Path to schema file
   * @returns {Promise<Object>} Form schema
   */
  async loadFormSchema(schemaPath) {
    try {
      const response = await fetch(schemaPath);
      if (!response.ok) {
        throw new Error(`Failed to load schema: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      throw new Error(`Error loading form schema: ${error.message}`);
    }
  }
}

export default FormValidator;
